import { z } from "zod";
import moment from "moment";
import type { TFunction } from "i18next";
import { serializeDay } from "./helpers";
import type { FormValues } from "./types";

const intervalSchema = z.object({
    from: z.date().nullable().optional(),
    to: z.date().nullable().optional(),
});

const dayDataSchema = z.object({
    allDay: z.boolean(),
    isAvailable: z.boolean(),
    reason: z.string().optional(),
    startDate: z.date().nullable().optional(),
    endDate: z.date().nullable().optional(),
    intervals: z.array(intervalSchema),
});

export const createReccurringSchema = (t: TFunction, weekdays: string[], initialValues?: FormValues) => {
    const shape = Object.fromEntries(weekdays.map((day) => [day, dayDataSchema]));

    return z.object(shape).superRefine((values: any, ctx) => {
        weekdays.forEach((day) => {
            const dayData = values[day];
            if (!dayData) return;

            const startDate = dayData.startDate as Date | null | undefined;
            const endDate = dayData.endDate as Date | null | undefined;

            // Dates are checked on every day the user edited - the same set the Save button
            // enables on and `onFinish` submits - so an edited row cannot be sent with a
            // past range, while untouched rows never block the form.
            if (serializeDay(dayData) !== serializeDay(initialValues?.[day])) {
                const today = moment().startOf("day");

                if (startDate && moment(startDate).isBefore(today, "day")) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [day, "startDate"], message: t("date-cannot-be-in-the-past") });
                }

                if (endDate) {
                    if (moment(endDate).isBefore(today, "day")) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [day, "endDate"], message: t("date-cannot-be-in-the-past") });
                    } else if (startDate && moment(endDate).isBefore(moment(startDate), "day")) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [day, "endDate"], message: t("end-date-cannot-be-before-start-date") });
                    }
                }
            }

            if (dayData.allDay) return;

            dayData.intervals?.forEach((interval, index) => {
                const prev = dayData.intervals[index - 1];
                const path = [day, "intervals", index];
                const from = interval.from as Date | undefined;
                const to = interval.to as Date | undefined;
                const prevFrom = prev?.from as Date | undefined;
                const prevTo = prev?.to as Date | undefined;

                if (from) {
                    if (to && from.getTime() >= to.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-smaller-than-end") });
                    } else if (prevFrom && prevTo && from.getTime() > prevFrom.getTime() && from.getTime() < prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-between-previous-start-and-end-time") });
                    } else if (prevFrom && from.getTime() === prevFrom.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("start-same-as-previous-start-time") });
                    } else if (!to) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "from"], message: t("required-end") });
                    }
                }

                if (to) {
                    if (from && to.getTime() <= from.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("end-must-be-same-or-bigger-than-start-time") });
                    } else if (prevFrom && prevTo && to.getTime() > prevFrom.getTime() && to.getTime() < prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("end-time-cannot-be-between-previous-start-and-end-time") });
                    } else if (from && prevFrom && prevTo && from.getTime() <= prevFrom.getTime() && to.getTime() >= prevTo.getTime()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("shifts-cannot-overlap-each-other") });
                    } else if (!from) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, "to"], message: t("required-start") });
                    }
                }
            });
        });
    });
};
